import React from 'react';
import { Archive, ArrowUpRight, Edit2, Gamepad2, MessageSquare, Star, Trash2, Trophy } from 'lucide-react';
import { STATUSES } from '../data/constants';

const statusAccent = {
  playing: 'bg-sky-400',
  completed: 'bg-emerald-400',
  backlog: 'bg-violet-400',
  dropped: 'bg-red-400'
};

const GameCard = ({ game, onEdit, onDelete, onToggleFavorite, minimal = false, compact = false }) => {
  const status = STATUSES.find(item => item.value === game.status) || STATUSES[0];

  if (compact) {
    return (
      <article
        className="flex items-center gap-3 p-2.5 rounded-2xl border border-white/[0.08] bg-[#12151d] cursor-pointer hover:border-white/[0.16] transition"
        onClick={() => onEdit(game)}
      >
        <div className={`w-1.5 h-10 rounded-full shrink-0 ${statusAccent[game.status] || 'bg-slate-500'} ${game.archived ? 'opacity-40' : ''}`} />
        <div className="w-11 h-11 rounded-lg overflow-hidden bg-[#1a1e28] shrink-0 grid place-items-center">
          {game.coverImage ? <img src={game.coverImage} alt="" className="w-full h-full object-cover" onError={e => { e.currentTarget.style.display='none'; }} /> : <Gamepad2 className="w-4 h-4 text-slate-700" />}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-white truncate">{game.name}</h3>
          <p className="text-[10px] text-slate-500 truncate">{status.label} · {game.platform}</p>
        </div>
        <div className="flex items-center gap-1 text-amber-300 shrink-0"><Star className="w-3.5 h-3.5 fill-current" /><b className="text-xs">{game.rating || 0}</b></div>
        {onToggleFavorite && (
          <button onClick={e => { e.stopPropagation(); onToggleFavorite(game); }} className={`icon-button !min-w-8 !min-h-8 shrink-0 ${game.favorite ? '!text-amber-300' : ''}`} aria-label={game.favorite ? 'Remover dos favoritos' : 'Marcar como favorito'} aria-pressed={!!game.favorite}>
            <Star className={`w-3.5 h-3.5 ${game.favorite ? 'fill-current' : ''}`} />
          </button>
        )}
      </article>
    );
  }

  return (
    <article
      className={`game-ticket group relative min-h-[174px] sm:min-h-[194px] overflow-hidden rounded-[24px] border border-white/[0.08] bg-[#12151d] cursor-pointer transition duration-300 hover:border-white/[0.16] hover:-translate-y-0.5 ${game.archived ? 'opacity-60' : ''}`}
      onClick={() => onEdit(game)}
    >
      <div className="absolute left-0 top-0 bottom-0 w-[38%] sm:w-[35%] overflow-hidden bg-[#1a1e28] isolate">
        {game.coverImage ? (
          <>
            <img
              src={game.coverImage}
              alt=""
              aria-hidden="true"
              className="absolute -inset-3 w-[calc(100%+24px)] h-[calc(100%+24px)] object-cover blur-xl scale-110 opacity-55"
            />
            <div className="absolute inset-0 bg-black/20" />
            <img
              src={game.coverImage}
              alt={`Capa de ${game.name}`}
              loading="lazy"
              decoding="async"
              className="relative z-[1] w-full h-full object-contain transition duration-700 group-hover:scale-[1.025]"
              onError={e => { e.currentTarget.style.display='none'; }}
            />
          </>
        ) : <div className="w-full h-full grid place-items-center text-slate-700"><Gamepad2 className="w-9 h-9" /></div>}
        <div className="absolute z-[2] inset-0 bg-gradient-to-r from-transparent via-transparent to-[#12151d]/90 pointer-events-none" />
      </div>

      <div className={`absolute left-[38%] sm:left-[35%] top-0 bottom-0 w-1 ${statusAccent[game.status] || 'bg-slate-500'}`} />

      <div className="relative ml-[38%] sm:ml-[35%] min-h-[174px] sm:min-h-[194px] p-4 sm:p-5 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span className="text-[9px] font-extrabold uppercase tracking-[.16em] text-slate-500">{status.label} · {game.platform}{game.archived ? ' · Arquivado' : ''}</span>
            <h3 className="text-base sm:text-lg font-extrabold text-white leading-tight mt-2 line-clamp-2">{game.name}</h3>
          </div>
          <ArrowUpRight className="w-4 h-4 text-slate-600 shrink-0 transition group-hover:text-lime-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>

        {game.tags?.length > 0 && <p className="hidden sm:block text-[10px] text-slate-600 mt-2 truncate">{game.tags.slice(0,3).join(' / ')}</p>}

        <div className="mt-auto flex items-end justify-between gap-2">
          <div>
            <div className="flex items-center gap-1 text-amber-300"><Star className="w-3.5 h-3.5 fill-current" /><b className="text-xs">{game.rating || 0}</b><span className="text-[9px] text-slate-600">/ 5</span></div>
            <div className="flex gap-2 mt-2">
              {game.platinado && <Trophy className="w-3.5 h-3.5 text-lime-400" />}
              {game.favorite && <Star className="w-3.5 h-3.5 text-amber-300 fill-amber-300" />}
              {game.archived && <Archive className="w-3.5 h-3.5 text-slate-500" />}
              {game.notes?.trim() && <MessageSquare className="w-3.5 h-3.5 text-slate-500" />}
            </div>
          </div>
          {!minimal && (onDelete || onToggleFavorite) && <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition">
            {onToggleFavorite && <button onClick={e => { e.stopPropagation(); onToggleFavorite(game); }} className={`icon-button !min-w-9 !min-h-9 ${game.favorite ? '!text-amber-300' : ''}`} aria-label={game.favorite ? 'Remover dos favoritos' : 'Marcar como favorito'} aria-pressed={!!game.favorite}><Star className={`w-3.5 h-3.5 ${game.favorite ? 'fill-current' : ''}`} /></button>}
            <button onClick={e => { e.stopPropagation(); onEdit(game); }} className="icon-button !min-w-9 !min-h-9" aria-label={`Editar ${game.name}`}><Edit2 className="w-3.5 h-3.5" /></button>
            {onDelete && <button onClick={e => { e.stopPropagation(); onDelete(game.id); }} className="icon-button !min-w-9 !min-h-9 hover:!text-red-300" aria-label={`Excluir ${game.name}`}><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>}
        </div>
      </div>
    </article>
  );
};
export default GameCard;
